import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from '../config/api';
import { errorMessage } from "../utils/errorMessage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import {
  ArrowLeft,
  Search,
  Loader2,
  X,
  ChevronRight,
  Sparkles,
  Plus,
  Trash2,
  Save,
  CheckCircle2,
  AlertCircle,
  FileText,
  Upload,
  AlertTriangle,
  Banknote,
} from "lucide-react";
import { toast } from "sonner";

import { LOGO_URL } from "../config/constants";
const num = (v) => parseFloat(v) || 0;
const fmt = (v) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 2 }).format(num(v));

// ========== Payment Row ==========
function PaymentRow({ item, idx, onChange, onRemove }) {
  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50/60">
      <td className="p-2">
        <Input
          value={item.description}
          onChange={(e) => onChange(idx, "description", e.target.value)}
          placeholder="e.g. Mortgage redemption"
          data-testid={`pmt-desc-${idx}`}
        />
      </td>
      <td className="p-2">
        <Input
          type="number"
          step="0.01"
          className="w-32 text-right"
          value={item.amount || ""}
          onChange={(e) => onChange(idx, "amount", e.target.value)}
          data-testid={`pmt-amount-${idx}`}
        />
      </td>
      <td className="p-2">
        <Input
          value={item.payee}
          onChange={(e) => onChange(idx, "payee", e.target.value)}
          placeholder="e.g. HMRC"
          data-testid={`pmt-payee-${idx}`}
        />
      </td>
      <td className="p-2">
        <Input
          value={item.payee_bank_details}
          onChange={(e) => onChange(idx, "payee_bank_details", e.target.value)}
          placeholder="Sort code / account"
          data-testid={`pmt-bank-${idx}`}
        />
      </td>
      <td className="p-2 w-10">
        <Button variant="ghost" size="sm" onClick={() => onRemove(idx)} className="text-status-red hover:text-status-red">
          <Trash2 className="h-4 w-4" />
        </Button>
      </td>
    </tr>
  );
}

// ========== STEP 1: Matter Selection ==========
function StepMatterSelection({ onSelect }) {
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [explanation, setExplanation] = useState("");

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    setResults(null);
    try {
      const res = await axios.post(`${API}/hub/ai-search`, { query: query.trim(), limit: 10 });
      setResults(res.data.results || []);
      setExplanation(res.data.explanation || "");
      if (!res.data.results?.length) toast.info("No matters found.");
    } catch {
      toast.error("Search failed. Please try again.");
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Brief 11c (29 May 2026): Completion Funds is being retired.
          Banner sits above the matter picker so it's the first thing
          users see on the page. Direct-URL access stays open for
          in-flight matters; historic detail views keep working. */}
      <div
        className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 mb-4 text-sm text-amber-900"
        data-testid="completion-funds-deprecation-banner"
      >
        <strong>Completion Funds is being retired.</strong> New requests should
        use <a href="/payment-request" className="underline font-medium" data-testid="completion-funds-deprecation-link">Payment Request</a>{" "}
        instead — it now covers both single-payment and completion scenarios.
        This page remains available for in-flight matters; it will be removed
        in a future release.
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg text-teal">Step 1 — Select Matter</CardTitle>
          <p className="text-sm text-gray-500">Search for the matter this completion funds request relates to.</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="flex gap-2 mb-6">
            <div className="relative flex-1">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g. 'VEC0089', 'Smith purchase', 'RM0306'"
                className="pr-8 border-indigo-200 focus:border-indigo-400 focus:ring-indigo-400"
                data-testid="ai-matter-search-input"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => { setQuery(""); setResults(null); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button
              type="submit"
              disabled={searching || !query.trim()}
              className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2"
              data-testid="ai-matter-search-btn"
            >
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Search
            </Button>
          </form>

          {results !== null && (
            <div>
              {explanation && (
                <p className="text-xs text-indigo-600 mb-3 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> {explanation}
                </p>
              )}
              {results.length > 0 ? (
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {results.map((r, i) => (
                    <button
                      key={r.id || i}
                      onClick={() => onSelect(r)}
                      className="w-full p-3 bg-white rounded-md border border-indigo-100 hover:border-indigo-300 hover:shadow-sm transition-all text-left flex items-center justify-between group"
                      data-testid={`ai-matter-result-${i}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs px-2 py-0.5 rounded bg-indigo-100 text-indigo-700">
                            {r.form_type_label || r.form_type}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            r.status === "approved" ? "bg-status-green-dim text-status-green" : "bg-status-amber-dim text-status-amber"
                          }`}>
                            {r.status}
                          </span>
                        </div>
                        <p className="font-medium text-white truncate">{r.secondary_label}</p>
                        <p className="text-sm text-slate-500 truncate">{r.primary_label} &middot; {r.tertiary_label}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-indigo-400 group-hover:translate-x-1 transition-transform flex-shrink-0 ml-2" />
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-center text-sm text-gray-500 py-4">No results for "{query}".</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ========== STEP 2: Form ==========
function StepForm({ initialMatter, onBack }) {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [errors, setErrors] = useState([]);

  // Header (pre-populated, read-only)
  const [matterRef, setMatterRef] = useState("");
  const [clientName, setClientName] = useState("");
  const [matterDescription, setMatterDescription] = useState("");
  const [feeEarner, setFeeEarner] = useState("");
  const [completionDate, setCompletionDate] = useState("");

  // Funds Summary (pre-populated, read-only)
  const [netBalance, setNetBalance] = useState(0);
  const [netDirection, setNetDirection] = useState("");
  const [totalFromClient, setTotalFromClient] = useState(0);
  const [totalToClient, setTotalToClient] = useState(0);

  // Client Bank Details (user-entered)
  const [accountName, setAccountName] = useState("");
  const [sortCode, setSortCode] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [bankName, setBankName] = useState("");
  const [reference, setReference] = useState("");

  // Timing Instructions (user-entered)
  const [completionTime, setCompletionTime] = useState("");
  const [fundsRequiredBy, setFundsRequiredBy] = useState("");
  const [urgent, setUrgent] = useState(false);
  const [specialInstructions, setSpecialInstructions] = useState("");

  // Payment Breakdown (pre-populated, editable)
  const [payments, setPayments] = useState([]);

  // Prefill from CS
  useEffect(() => {
    if (!initialMatter) return;

    const ref = initialMatter.secondary_label
      || initialMatter.matter_reference
      || initialMatter.data?.Q4
      || initialMatter.data?.Q3
      || initialMatter.data?.header?.matter_reference
      || "";

    if (initialMatter.primary_label) setClientName(initialMatter.primary_label);
    setMatterRef(ref);

    if (ref) {
      axios.get(`${API}/accountsinstruction/prefill/${encodeURIComponent(ref)}`)
        .then((res) => {
          if (res.data.found) {
            const p = res.data.prefill;
            const h = p.header || {};
            setMatterRef(h.matterref || ref);
            setClientName(h.client_name || initialMatter.primary_label || "");
            setMatterDescription(h.matter_description || "");
            setFeeEarner(h.fee_earner || "");
            setCompletionDate(h.completion_date || "");

            const fs = p.funds_summary || {};
            setNetBalance(fs.net_completion_balance || 0);
            setNetDirection(fs.net_direction || "");
            setTotalFromClient(fs.total_due_from_client || 0);
            setTotalToClient(fs.total_due_to_client || 0);

            const pmts = (p.payment_breakdown || []).map((pm, i) => ({ ...pm, id: `prefill-${i}` }));
            setPayments(pmts);

            toast.success("Form pre-populated from latest completion statement");
          } else {
            toast.info("No completion statement found — please enter details manually");
          }
        })
        .catch(() => {
          toast.error("Failed to pre-populate from completion statement");
        });
    }
  }, [initialMatter]);

  const addPayment = () => {
    setPayments([...payments, { id: `pmt-${Date.now()}`, description: "", amount: "", payee: "", payee_bank_details: "" }]);
  };
  const removePayment = (idx) => setPayments(payments.filter((_, i) => i !== idx));
  const changePayment = (idx, field, value) => {
    const copy = [...payments];
    copy[idx] = { ...copy[idx], [field]: value };
    setPayments(copy);
  };

  const totalPayments = useMemo(() => payments.reduce((s, p) => s + num(p.amount), 0), [payments]);

  const buildFormData = useCallback(() => ({
    header: {
      matterref: matterRef,
      client_name: clientName,
      matter_description: matterDescription,
      fee_earner: feeEarner,
      completion_date: completionDate,
    },
    funds_summary: {
      net_completion_balance: netBalance,
      net_direction: netDirection,
      total_due_from_client: totalFromClient,
      total_due_to_client: totalToClient,
    },
    client_bank_details: {
      account_name: accountName,
      sort_code: sortCode,
      account_number: accountNumber,
      bank_name: bankName,
      reference,
    },
    timing_instructions: {
      completion_time: completionTime,
      funds_required_by: fundsRequiredBy,
      urgent,
      special_instructions: specialInstructions,
    },
    payment_breakdown: payments.map((p) => ({
      description: p.description,
      amount: num(p.amount),
      payee: p.payee,
      payee_bank_details: p.payee_bank_details,
    })),
  }), [matterRef, clientName, matterDescription, feeEarner, completionDate,
    netBalance, netDirection, totalFromClient, totalToClient,
    accountName, sortCode, accountNumber, bankName, reference,
    completionTime, fundsRequiredBy, urgent, specialInstructions, payments]);

  const handleSaveDraft = async () => {
    setSaving(true);
    setErrors([]);
    try {
      const res = await axios.post(`${API}/accountsinstruction`, buildFormData());
      if (res.data?.success) {
        toast.success("Draft saved successfully");
        navigate(`/submission/${res.data.id}`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.validation_errors) {
        setErrors(detail.validation_errors);
        toast.error("Please fix the validation errors");
      } else {
        toast.error(errorMessage(err, "Failed to save draft"));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateSubmit = async () => {
    setGenerating(true);
    setErrors([]);
    try {
      // First save, then generate
      const saveRes = await axios.post(`${API}/accountsinstruction`, buildFormData());
      if (!saveRes.data?.success) {
        toast.error("Failed to save instruction");
        return;
      }
      const id = saveRes.data.id;

      // Generate document
      const genRes = await axios.post(`${API}/accountsinstruction/${id}/generate`);
      if (genRes.data?.success) {
        let msg = "Document generated and submitted.";
        if (genRes.data.sharepoint?.uploaded) msg += " Filed to SharePoint.";
        if (genRes.data.email?.status === "sent") msg += " Email sent to Accounts.";
        toast.success(msg);
        navigate(`/submission/${id}`);
      } else {
        toast.error("Generation failed");
        navigate(`/submission/${id}`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.validation_errors) {
        setErrors(detail.validation_errors);
        toast.error("Please fix the validation errors");
      } else {
        toast.error(errorMessage(err, "Failed to generate"));
      }
    } finally {
      setGenerating(false);
    }
  };

  const directionLabel = {
    BalanceRequiredFromClient: "Balance required from client",
    BalanceDueToClient: "Balance due to client",
    Balanced: "Balanced (nil due)",
  }[netDirection] || netDirection;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Errors */}
      {errors.length > 0 && (
        <div className="bg-status-red-dim border border-red-200 rounded-md p-4" data-testid="ai-validation-errors">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-4 h-4 text-status-red" />
            <span className="text-sm font-medium text-status-red">Please fix the following:</span>
          </div>
          <ul className="list-disc list-inside text-sm text-status-red space-y-1">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      {/* Header (read-only) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base text-teal flex items-center gap-2">
            <FileText className="w-4 h-4" /> Matter Details
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-gray-500">Matter Reference</Label>
            <Input value={matterRef} readOnly className="bg-gray-50" data-testid="ai-matterref" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Client Name</Label>
            <Input value={clientName} readOnly className="bg-gray-50" data-testid="ai-clientname" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Matter Description</Label>
            <Input value={matterDescription} onChange={(e) => setMatterDescription(e.target.value)} data-testid="ai-matter-desc" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Fee Earner</Label>
            <Input value={feeEarner} readOnly className="bg-gray-50" data-testid="ai-fee-earner" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Completion Date</Label>
            <Input type="date" value={completionDate} onChange={(e) => setCompletionDate(e.target.value)} data-testid="ai-completion-date" />
          </div>
        </CardContent>
      </Card>

      {/* Funds Summary (read-only) */}
      <Card className="border-teal/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-teal flex items-center gap-2">
            <Banknote className="w-4 h-4" /> Funds Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-teal/5 border border-teal/20 rounded-md p-4 space-y-3" data-testid="ai-funds-summary">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Completion Balance</span>
              <span className="text-xl font-bold text-teal tabular-nums" data-testid="ai-net-balance">{fmt(netBalance)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Direction</span>
              <span className="text-sm font-medium text-gray-800">{directionLabel}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">From Client</span>
              <span className="text-sm tabular-nums">{fmt(totalFromClient)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">To Client</span>
              <span className="text-sm tabular-nums">{fmt(totalToClient)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Client Bank Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base text-indigo-700">Client Bank Details</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-gray-500">Account Name *</Label>
            <Input value={accountName} onChange={(e) => setAccountName(e.target.value)}
              placeholder="e.g. Mr John Smith" data-testid="ai-account-name" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Sort Code *</Label>
            <Input value={sortCode} onChange={(e) => setSortCode(e.target.value)}
              placeholder="XX-XX-XX" data-testid="ai-sort-code" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Account Number *</Label>
            <Input value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)}
              placeholder="8 digits" data-testid="ai-account-number" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Bank Name</Label>
            <Input value={bankName} onChange={(e) => setBankName(e.target.value)}
              placeholder="e.g. Barclays Bank" data-testid="ai-bank-name" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-gray-500">Payment Reference</Label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)}
              placeholder="Reference for transfer to client" data-testid="ai-reference" />
          </div>
        </CardContent>
      </Card>

      {/* Timing Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base text-indigo-700">Timing Instructions</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-gray-500">Completion Time</Label>
            <Input type="time" value={completionTime} onChange={(e) => setCompletionTime(e.target.value)}
              data-testid="ai-completion-time" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Funds Required By *</Label>
            <Input type="datetime-local" value={fundsRequiredBy} onChange={(e) => setFundsRequiredBy(e.target.value)}
              data-testid="ai-funds-required-by" />
          </div>
          <div className="col-span-2 flex items-center gap-3">
            <input
              type="checkbox"
              id="urgent"
              checked={urgent}
              onChange={(e) => setUrgent(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-status-red focus:ring-red-500"
              data-testid="ai-urgent-checkbox"
            />
            <label htmlFor="urgent" className="text-sm text-gray-700 flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4 text-status-red" />
              Urgent — same-day / priority processing
            </label>
          </div>
          {urgent && (
            <div className="col-span-2 bg-status-red-dim border border-red-200 rounded-md p-3 text-sm text-status-red" data-testid="ai-urgent-warning">
              Please notify Accounts separately that this is an urgent instruction.
            </div>
          )}
          <div className="col-span-2">
            <Label className="text-xs text-gray-500">Special Instructions</Label>
            <textarea
              value={specialInstructions}
              onChange={(e) => setSpecialInstructions(e.target.value)}
              className="w-full border rounded-md p-3 text-sm min-h-[80px] resize-y border-gray-200 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 outline-none"
              placeholder="Any specific timing or process notes..."
              data-testid="ai-special-instructions"
            />
          </div>
        </CardContent>
      </Card>

      {/* Payment Breakdown */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base text-indigo-700">Payment Breakdown</CardTitle>
            <Button variant="outline" size="sm" onClick={addPayment} data-testid="ai-add-payment">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Payment
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-gray-100 text-left">
                  <th className="p-2 font-medium text-gray-600">Description</th>
                  <th className="p-2 font-medium text-gray-600 w-32">Amount</th>
                  <th className="p-2 font-medium text-gray-600">Payee</th>
                  <th className="p-2 font-medium text-gray-600">Bank Details</th>
                  <th className="p-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p, idx) => (
                  <PaymentRow key={p.id} item={p} idx={idx} onChange={changePayment} onRemove={removePayment} />
                ))}
                {payments.length === 0 && (
                  <tr>
                    <td colSpan={5} className="p-4 text-center text-gray-400 italic text-xs">
                      No payments. Click "Add Payment" to add.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {payments.length > 0 && (
            <div className="mt-3 text-right text-sm font-semibold text-teal" data-testid="ai-total-payments">
              Total Payments: {fmt(totalPayments)}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex justify-between items-center pt-2 pb-8">
        <Button variant="outline" onClick={onBack} data-testid="ai-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleSaveDraft}
            disabled={saving || generating}
            data-testid="ai-save-draft-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save Draft
          </Button>
          <Button
            className="bg-teal hover:bg-teal-hover"
            onClick={handleGenerateSubmit}
            disabled={saving || generating}
            data-testid="ai-generate-submit-btn"
          >
            {generating ? (
              <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Generating...</>
            ) : (
              <><Upload className="w-4 h-4 mr-1" /> Generate &amp; Submit</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ========== Main Page ==========
export default function AccountsInstructionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [selectedMatter, setSelectedMatter] = useState(null);

  // If navigated with matter_id query param (e.g. from revision), load that matter
  useEffect(() => {
    const matterId = searchParams.get("matter_id");
    if (matterId) {
      axios.get(`${API}/accountsinstruction/${matterId}`)
        .then((res) => {
          if (res.data) {
            setSelectedMatter(res.data);
            setStep(2);
          }
        })
        .catch(() => {});
    }
  }, [searchParams]);

  const handleMatterSelected = (matter) => {
    setSelectedMatter(matter);
    setStep(2);
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      {/* Nav */}
      <nav className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-14">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} className="flex items-center gap-2 hover:opacity-80">
              <img src={LOGO_URL} alt="Bonsai Law" className="w-8 h-8" />
              <span className="text-sm font-bold text-teal">Bonsai Law</span>
            </button>
            <span className="text-gray-300">/</span>
            <span className="text-sm text-gray-700">Completion Funds Request</span>
          </div>
          <div className="flex items-center gap-3">
            {step === 2 && (
              <span className="text-xs text-gray-400">Step 2 of 2</span>
            )}
            <Button variant="ghost" size="sm" onClick={() => navigate("/")} data-testid="ai-nav-dashboard">
              <ArrowLeft className="h-4 w-4 mr-1" /> Dashboard
            </Button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="px-4 sm:px-6 lg:px-8 py-8" data-testid="accounts-instruction-page">
        {step === 1 && <StepMatterSelection onSelect={handleMatterSelected} />}
        {step === 2 && <StepForm initialMatter={selectedMatter} onBack={() => setStep(1)} />}
      </main>
    </div>
  );
}
